import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "../styles/AdminUsersPage.css";
import { API_BASE } from "../config";
import { getSession } from "../session";
import apiFetch from "../apiFetch";

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function toDateInput(d) {
  if (!d) return "";
  return String(d).slice(0, 10); // strip time part if present
}

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [pageStatus, setPageStatus] = useState("loading");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [actionError, setActionError] = useState("");

  // Inline subscription-end editing
  const [editingSubId, setEditingSubId] = useState(null);
  const [subEndDraft, setSubEndDraft] = useState("");
  const deleteModalRef = useRef(null);

  const session = getSession();

  useEffect(() => {
    if (!deleteTarget) return;
    const el = deleteModalRef.current;
    if (!el) return;
    const focusable = el.querySelectorAll('button, [href], input, [tabindex]:not([tabindex="-1"])');
    focusable[0]?.focus();
    const handler = (e) => {
      if (e.key === "Escape") setDeleteTarget(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [deleteTarget]);

  const uid = session?.userId ?? "";
  const jsonHeaders = { "Content-Type": "application/json" };

  const fetchUsers = async () => {
    setPageStatus("loading");
    try {
      const res = await apiFetch(`/api/admin/users?adminUserId=${uid}`, { headers: jsonHeaders });
      if (res.status === 401 || res.status === 403) { setPageStatus("denied"); return; }
      if (!res.ok) { setPageStatus("error"); return; }
      const data = await res.json();
      setUsers(data);
      setPageStatus("ok");
    } catch {
      setPageStatus("error");
    }
  };

  useEffect(() => {
    if (!session) { setPageStatus("denied"); return; }
    fetchUsers();
  }, []); // intentionally run once on mount

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      users.filter(
        (u) =>
          u.username.toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q)
      )
    );
  }, [search, users]);

  const toggleStatus = async (user) => {
    setActionLoading(user.id);
    setActionError("");
    try {
      const res = await apiFetch(`/api/admin/users/${user.id}/status`, {
        method: "PATCH",
        headers: jsonHeaders,
        body: JSON.stringify({ adminUserId: uid, active: user.active ? 0 : 1 }),
      });
      const data = await res.json();
      if (res.ok) {
        setUsers((prev) =>
          prev.map((u) => (u.id === user.id ? { ...u, active: user.active ? 0 : 1 } : u))
        );
      } else {
        setActionError(data.error || "Failed to update status.");
      }
    } catch {
      setActionError("Request failed. Please try again.");
    } finally {
      setActionLoading(null);
    }
  };

  const startEditSub = (user) => {
    setEditingSubId(user.id);
    setSubEndDraft(toDateInput(user.subscription_end));
    setActionError("");
  };

  const cancelEditSub = () => {
    setEditingSubId(null);
    setSubEndDraft("");
  };

  const saveSubEnd = async (userId) => {
    setActionLoading(`sub-${userId}`);
    setActionError("");
    try {
      const res = await apiFetch(`/api/admin/users/${userId}/subscription`, {
        method: "PATCH",
        headers: jsonHeaders,
        body: JSON.stringify({ adminUserId: uid, subscriptionEnd: subEndDraft || null }),
      });
      const data = await res.json();
      if (res.ok) {
        setUsers((prev) =>
          prev.map((u) =>
            u.id === userId ? { ...u, subscription_end: subEndDraft || null } : u
          )
        );
        setEditingSubId(null);
        setSubEndDraft("");
      } else {
        setActionError(data.error || "Failed to update subscription.");
      }
    } catch {
      setActionError("Request failed. Please try again.");
    } finally {
      setActionLoading(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(deleteTarget.id);
    setActionError("");
    try {
      const res = await apiFetch(`/api/admin/users/${deleteTarget.id}?adminUserId=${uid}`, {
        method: "DELETE",
        headers: jsonHeaders,
      });
      const data = await res.json();
      if (res.ok) {
        setUsers((prev) => prev.filter((u) => u.id !== deleteTarget.id));
        setDeleteTarget(null);
      } else {
        setActionError(data.error || "Failed to delete user.");
        setDeleteTarget(null);
      }
    } catch {
      setActionError("Request failed. Please try again.");
      setDeleteTarget(null);
    } finally {
      setActionLoading(null);
    }
  };

  if (pageStatus === "loading") {
    return <div className="adm-page"><div className="adm-center"><p className="adm-muted">Loading…</p></div></div>;
  }

  if (pageStatus === "denied") {
    return (
      <div className="adm-page">
        <div className="adm-center">
          <div className="adm-denied-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <h2 className="adm-denied-title">Access Denied</h2>
          <p className="adm-muted">You do not have permission to view this page.</p>
        </div>
      </div>
    );
  }

  if (pageStatus === "error") {
    return (
      <div className="adm-page">
        <div className="adm-center">
          <p className="adm-muted">Failed to load users.</p>
          <button className="adm-btn adm-btn-secondary" onClick={fetchUsers}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="adm-page">
      <div className="adm-header">
        <div>
          <h1 className="adm-title">User Management</h1>
          <p className="adm-muted">{users.length} account{users.length !== 1 ? "s" : ""}</p>
        </div>
        <button className="adm-btn adm-btn-primary" onClick={fetchUsers}>Refresh</button>
      </div>

      {actionError && <div className="adm-error-banner">{actionError}</div>}

      <div className="adm-toolbar">
        <div className="adm-search-wrap">
          <svg className="adm-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            className="adm-search"
            type="text"
            placeholder="Search by username or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {search && (
          <p className="adm-muted" style={{ margin: 0, alignSelf: "center", fontSize: "0.8rem" }}>
            {filtered.length} result{filtered.length !== 1 ? "s" : ""}
          </p>
        )}
      </div>

      <div className="adm-table-wrap">
        <table className="adm-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Username</th>
              <th>Email</th>
              <th>Status</th>
              <th>Role</th>
              <th>Auth</th>
              <th>Sub Start</th>
              <th>Sub End</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="adm-empty">No users found.</td>
              </tr>
            )}
            {filtered.map((user) => {
              const isSelf = user.id === session?.userId;
              const busy = actionLoading === user.id;
              const subBusy = actionLoading === `sub-${user.id}`;
              const isEditingSub = editingSubId === user.id;
              const subExpired = user.subscription_end && new Date(user.subscription_end) < new Date();

              return (
                <tr key={user.id} className={!user.active ? "adm-row-inactive" : ""}>
                  <td className="adm-muted-cell">{user.id}</td>
                  <td>
                    <Link to={`/admin/users/${user.id}/report`} className="adm-user-link">
                      <strong>{user.username}</strong>
                    </Link>
                    {isSelf && <span className="adm-tag-self">you</span>}
                  </td>
                  <td className="adm-muted-cell">{user.email || "—"}</td>
                  <td>
                    <span className={`adm-badge ${user.active ? "adm-badge-active" : "adm-badge-inactive"}`}>
                      {user.active ? "Active" : "Disabled"}
                    </span>
                  </td>
                  <td>
                    {user.is_admin
                      ? <span className="adm-badge adm-badge-admin">Admin</span>
                      : <span className="adm-muted-cell">User</span>}
                  </td>
                  <td>
                    {user.is_google
                      ? <span className="adm-badge adm-badge-google">Google</span>
                      : <span className="adm-muted-cell">Password</span>}
                  </td>

                  {/* Subscription start — read only */}
                  <td className="adm-muted-cell">{fmtDate(user.subscription_start)}</td>

                  {/* Subscription end — inline editable */}
                  <td>
                    {isEditingSub ? (
                      <div className="adm-sub-edit">
                        <input
                          type="date"
                          className="adm-date-input"
                          value={subEndDraft}
                          onChange={(e) => setSubEndDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveSubEnd(user.id);
                            if (e.key === "Escape") cancelEditSub();
                          }}
                          autoFocus
                        />
                        <button
                          className="adm-icon-btn adm-icon-btn-ok"
                          onClick={() => saveSubEnd(user.id)}
                          disabled={subBusy}
                          title="Save"
                        >
                          {subBusy ? "…" : "✓"}
                        </button>
                        <button
                          className="adm-icon-btn adm-icon-btn-cancel"
                          onClick={cancelEditSub}
                          disabled={subBusy}
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className="adm-sub-display">
                        <span className={subExpired ? "adm-sub-expired" : ""}>
                          {fmtDate(user.subscription_end)}
                        </span>
                        <button
                          className="adm-edit-btn"
                          onClick={() => startEditSub(user)}
                          title="Edit subscription end date"
                        >
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 112.828 2.828L11.828 15.828a2 2 0 01-1.414.586H9v-2.414a2 2 0 01.586-1.414z" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </td>

                  <td className="adm-actions">
                    <button
                      className={`adm-btn ${user.active ? "adm-btn-warn" : "adm-btn-success"}`}
                      onClick={() => toggleStatus(user)}
                      disabled={busy || isSelf}
                      title={isSelf ? "Cannot modify your own account" : ""}
                    >
                      {busy ? "…" : user.active ? "Disable" : "Enable"}
                    </button>
                    <button
                      className="adm-btn adm-btn-danger"
                      onClick={() => setDeleteTarget(user)}
                      disabled={busy || isSelf}
                      title={isSelf ? "Cannot delete your own account" : ""}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="adm-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div
            className="adm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="adm-delete-title"
            ref={deleteModalRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="adm-modal-icon" aria-hidden="true">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h3 id="adm-delete-title" className="adm-modal-title">Delete Account</h3>
            <p className="adm-modal-body">
              Permanently delete <strong>{deleteTarget.username}</strong>? All their game history,
              coins, and progress will be removed. This cannot be undone.
            </p>
            <div className="adm-modal-actions">
              <button
                className="adm-btn adm-btn-secondary"
                onClick={() => setDeleteTarget(null)}
                disabled={actionLoading === deleteTarget.id}
              >
                Cancel
              </button>
              <button
                className="adm-btn adm-btn-danger"
                onClick={confirmDelete}
                disabled={actionLoading === deleteTarget.id}
              >
                {actionLoading === deleteTarget.id ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
